import { useCallback, useEffect, useState } from 'react';

import { api, type FelrakodasSor, type SoforHordo } from '../api.ts';
import { useFelhasznalo, useMuvelet } from '../felhasznalo-context.ts';
import { szam } from '../formazas.ts';

/**
 * Felrakodás rögzítése az autóra. (Megbízás 6.6 / 2026-11-04)
 *
 * A megrendelő kérése: „a raktáros feltesz például 30 üres hordót Ferire,
 * ahogy Feri az adott napon állítja ki a szállítóit, úgy az autókészletben
 * folyamatosan nyomon lehet követni, mennyi üres hordója van még."
 *
 * Alkalmanként rögzítünk, nem naponta: „van olyan, hogy valaki egy nap
 * kétszer is lepakol és felpakol". Ezért minden felrakodás önálló sor.
 *
 * Amit nem kell beírni: a kiszállított liter a járatlapról jön, az üres
 * hordó fogyása a szállítóleveleken otthagyott hordókból. A raktáros csak
 * azt írja be, amit tényleg feltesz.
 */

/** A felrakodás joga. A raktáros és a diszpécser kapja; a szerver is ezt nézi. */
export function useFelrakodhat(): boolean {
  const f = useFelhasznalo();
  const sajat = useMuvelet('keszlet_felrakodas');
  /* A két legközelebbi meglévő jog is elég — így a frissítés után azonnal
     használható, mielőtt valaki kipipálja az újat a szerepköröknél. */
  const kiadas = useMuvelet('kiadas');
  const masAutoja = useMuvelet('auto_kiurit_mas');
  return !!f && (sajat || kiadas || masAutoja);
}

export function Felrakodas({
  autok,
  onKesz,
}: {
  autok: Array<{ sofor_id: number; nev: string; rendszam: string | null; aktiv: boolean }>;
  /** Rögzítés után a napló újratöltéséhez. */
  onKesz: () => void;
}) {
  const felrakodhat = useFelrakodhat();
  const admin = !!useFelhasznalo()?.admin;

  const [allasok, setAllasok] = useState<SoforHordo[]>([]);
  const [sorok, setSorok] = useState<FelrakodasSor[]>([]);
  const [ma, setMa] = useState('');
  const [soforId, setSoforId] = useState<number | null>(null);
  const [liter, setLiter] = useState('');
  const [hordo, setHordo] = useState('');
  const [megjegyzes, setMegjegyzes] = useState('');
  const [korrekcio, setKorrekcio] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [mentes, setMentes] = useState(false);

  const betolt = useCallback(async () => {
    try {
      const [h, f] = await Promise.all([
        api.get<{ sorok: SoforHordo[] }>('/api/keszlet/hordo'),
        api.get<{ sorok: FelrakodasSor[]; ma: string }>('/api/keszlet/felrakodas'),
      ]);
      setAllasok(h.sorok);
      setSorok(f.sorok);
      setMa(f.ma);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a felrakodásokat.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  useEffect(() => {
    if (soforId === null) setSoforId(autok.find((a) => a.aktiv)?.sofor_id ?? null);
  }, [autok, soforId]);

  const allas = allasok.find((a) => a.sofor_id === soforId) ?? null;

  async function rogzit() {
    if (soforId === null || mentes) return;
    setMentes(true);
    setHiba(null);
    setUzenet(null);
    try {
      const v = await api.post<{ hordo: SoforHordo }>('/api/keszlet/felrakodas', {
        sofor_id: soforId,
        liter: korrekcio ? '' : liter,
        ures_hordo: hordo,
        megjegyzes,
        fajta: korrekcio ? 'korrekcio' : 'felrakodas',
      });
      setLiter('');
      setHordo('');
      setMegjegyzes('');
      setUzenet(
        korrekcio
          ? `${v.hordo.nev}: a hordó darabszáma ${v.hordo.ures} db-ra állítva.`
          : `${v.hordo.nev} felrakodása rögzítve. Üres hordó az autón: ${
              v.hordo.ures === null ? 'nincs felmérve' : `${v.hordo.ures} db`
            }.`,
      );
      await betolt();
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A rögzítés nem sikerült.');
    } finally {
      setMentes(false);
    }
  }

  async function torol(id: number) {
    try {
      await api.torol(`/api/keszlet/felrakodas/${id}`);
      await betolt();
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  return (
    <div className="npform" style={{ maxWidth: 760, marginTop: 12 }}>
      <h4>Felrakodás az autóra</h4>
      <div className="c" style={{ marginBottom: 10 }}>
        Azt kell beírni, amit a raktáros <b>feltesz</b>: olajat literben, üres hordót darabban.
        Egy napon többször is rögzíthető — ahányszor tényleg felpakolnak. A fogyás magától jön:
        a kiszállított liter a járatlapról, az üres hordó a szállítóleveleken otthagyott
        hordókból.
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {/* --- a sofőrök mostani állása --- */}
      <table className="tt2" style={{ marginBottom: 12 }}>
        <thead>
          <tr>
            <th>Sofőr</th>
            <th className="n">Üres hordó</th>
            <th className="n">Tele hordó az autón</th>
            <th>Utolsó felrakodás</th>
          </tr>
        </thead>
        <tbody>
          {allasok.map((a) => (
            <tr key={a.sofor_id} className={a.sofor_id === soforId ? 'kijelolt' : undefined}>
              <td>{a.nev}</td>
              <td className="calc2">
                {a.ures === null ? <span className="halvany">nincs felmérve</span> : <b>{szam(a.ures, 0)}</b>}
              </td>
              <td className="calc2">{a.tele ? szam(a.tele, 0) : '·'}</td>
              <td className="halvany">{a.utolso_felrakodas ?? '—'}</td>
            </tr>
          ))}
          {allasok.length === 0 && (
            <tr>
              <td colSpan={4} className="halvany" style={{ padding: 14, textAlign: 'center' }}>
                Nincs aktív sofőr.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {!felrakodhat ? (
        <div className="hint">
          A felrakodás rögzítéséhez engedély kell. A számokat látod, beírni nem tudsz.
        </div>
      ) : (
        <>
          <div className="tetelmezok">
            <label>
              Sofőr
              <select
                value={soforId ?? ''}
                onChange={(e) => setSoforId(e.target.value ? Number(e.target.value) : null)}
              >
                {autok
                  .filter((a) => a.aktiv)
                  .map((a) => (
                    <option key={a.sofor_id} value={a.sofor_id}>
                      {a.nev}
                      {a.rendszam ? ` · ${a.rendszam}` : ''}
                    </option>
                  ))}
              </select>
            </label>
            {!korrekcio && (
              <label>
                Feltett olaj (liter)
                <input
                  className="n"
                  value={liter}
                  onChange={(e) => setLiter(e.target.value)}
                  placeholder="0"
                />
              </label>
            )}
            <label>
              {korrekcio ? 'Megszámolt üres hordó (db)' : 'Feltett üres hordó (db)'}
              <input
                className="n"
                value={hordo}
                onChange={(e) => setHordo(e.target.value)}
                placeholder="0"
              />
            </label>
            <label style={{ minWidth: 200 }}>
              Megjegyzés
              <input value={megjegyzes} onChange={(e) => setMegjegyzes(e.target.value)} />
            </label>
          </div>

          {/*
            * A korrekció nem hozzáad, hanem leszögezi a darabszámot: ha egy
            * darabig nem vezették következetesen, ezzel lehet újra igazzá
            * tenni anélkül, hogy a múltat át kellene írni. Csak adminisztrátor.
            */}
          {admin && (
            <label className="kapcs" style={{ marginTop: 6 }}>
              <input
                type="checkbox"
                checked={korrekcio}
                onChange={(e) => setKorrekcio(e.target.checked)}
              />
              Megszámoltuk — ennyi üres hordó van most az autón (korrekció)
            </label>
          )}

          {allas && (
            <div className="hint" style={{ marginTop: 6 }}>
              {allas.nev} autóján{' '}
              {allas.ures === null ? (
                <b>még nincs felmérve</b>
              ) : (
                <>
                  most <b>{allas.ures} üres hordó</b> van
                </>
              )}
              {!korrekcio && hordo && Number(hordo) > 0 && (
                <>
                  {' '}
                  → a felrakodás után <b>{(allas.ures ?? 0) + Math.round(Number(hordo))} db</b>
                </>
              )}
              . Ha a valóság más, az adminisztrátor a korrekcióval tudja leszögezni.
            </div>
          )}

          <div className="btns" style={{ marginTop: 8 }}>
            <button className="btn pri sm" disabled={mentes || soforId === null} onClick={() => void rogzit()}>
              {korrekcio ? 'Darabszám leszögezése' : 'Felrakodás rögzítése'}
            </button>
          </div>
        </>
      )}

      {/* --- a mai és korábbi rögzítések --- */}
      <h4 style={{ marginTop: 16 }}>Rögzített felrakodások</h4>
      <table className="tt2">
        <thead>
          <tr>
            <th>Nap</th>
            <th>Sofőr</th>
            <th className="n">Liter</th>
            <th className="n">Üres hordó</th>
            <th>Megjegyzés</th>
            <th>Rögzítette</th>
            {admin && <th />}
          </tr>
        </thead>
        <tbody>
          {sorok.slice(0, 40).map((s) => (
            <tr key={s.id}>
              <td>
                {s.datum}
                {s.datum === ma && <span className="elojel"> ma</span>}
              </td>
              <td>
                {s.sofor_nev}
                {s.fajta === 'korrekcio' && <span className="elojel"> korrekció</span>}
              </td>
              <td className="calc2">{s.liter === null ? '·' : szam(s.liter, 1)}</td>
              <td className="calc2">{s.ures_hordo === null ? '·' : szam(s.ures_hordo, 0)}</td>
              <td className="halvany">{s.megjegyzes ?? ''}</td>
              <td className="halvany">{s.rogzitette}</td>
              {admin && (
                <td>
                  <button
                    className="btn sm"
                    onClick={() => void torol(s.id)}
                    title="A felrakodás törlése — csak adminisztrátor"
                  >
                    Törlés
                  </button>
                </td>
              )}
            </tr>
          ))}
          {sorok.length === 0 && (
            <tr>
              <td colSpan={admin ? 7 : 6} className="halvany" style={{ padding: 14, textAlign: 'center' }}>
                Még nincs rögzített felrakodás.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {sorok.length > 40 && (
        <div className="hint">A legutóbbi 40 sor látszik ({sorok.length} van összesen).</div>
      )}
    </div>
  );
}
